import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { MessageCircle, Zap } from "lucide-react";
import axiosClient from "../../api/axiosClient";
import TypewriterHeadline from "../../components/TypewriterHeadline";

const SIGNUP_OTP_CONFIG_TOKEN =
  process.env.REACT_APP_SIGNUP_OTP_CONFIG_TOKEN ||
  process.env.VITE_SIGNUP_OTP_CONFIG_TOKEN ||
  "";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const otpRefs = useRef([]);

  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [verificationToken, setVerificationToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  const friendlyOtpError = message => {
    const m = String(message || "");
    const u = m.toUpperCase();
    if (u.includes("OTP_HOURLY_LIMIT_REACHED")) {
      return "You have exceeded your OTP send limit. Please try again after 1 hour.";
    }
    if (u.includes("OTP_DAILY_LIMIT_REACHED")) {
      return "You have exceeded your OTP send limit for today. Please try again tomorrow.";
    }
    if (u.includes("OTP_RESEND_COOLDOWN")) {
      return "Please wait before requesting another OTP.";
    }
    if (u.includes("OTP_INVALID")) return "The OTP entered is incorrect.";
    if (u.includes("OTP_EXPIRED")) return "OTP has expired. Please request a new one.";
    return m || "OTP request failed.";
  };

  const canSendOtp =
    email.trim() &&
    /^\S+@\S+\.\S+$/.test(email.trim()) &&
    phone.length === 10 &&
    !otpLoading &&
    resendCooldown === 0;

  const canReset =
    otpVerified &&
    newPassword.length >= 6 &&
    confirmPassword.length >= 6 &&
    newPassword === confirmPassword &&
    !loading;

  const handlePhoneChange = e => {
    const digits = String(e.target.value || "").replace(/\D/g, "").slice(0, 10);
    setPhone(digits);
  };

  const handleSendOtp = async () => {
    setError("");
    if (!SIGNUP_OTP_CONFIG_TOKEN) {
      setError("OTP config token is missing. Contact support.");
      return;
    }
    if (!canSendOtp) return;

    setOtpLoading(true);
    try {
      const res = await axiosClient.post(
        "/auth/forgot-password/phone-otp/send",
        { phone, configToken: SIGNUP_OTP_CONFIG_TOKEN },
        { __silentToast: true },
      );
      const ok = res?.data?.success ?? res?.data?.Success ?? false;
      if (!ok) throw new Error(res?.data?.message || "Failed to send OTP.");

      setOtpSent(true);
      setOtpVerified(false);
      setVerificationToken("");
      setOtp("");
      setResendCooldown(60);
      toast.success("OTP sent to your mobile number.");
    } catch (err) {
      setError(friendlyOtpError(err?.response?.data?.message || err?.message));
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError("");
    if (!SIGNUP_OTP_CONFIG_TOKEN) {
      setError("OTP config token is missing. Contact support.");
      return;
    }
    if (otp.length !== 6) {
      setError("Enter the 6-digit OTP.");
      return;
    }

    setOtpLoading(true);
    try {
      const res = await axiosClient.post(
        "/auth/signup-phone-otp/verify",
        { phone, otp, configToken: SIGNUP_OTP_CONFIG_TOKEN },
        { __silentToast: true },
      );
      const ok = res?.data?.success ?? res?.data?.Success ?? false;
      if (!ok) throw new Error(res?.data?.message || "OTP verification failed.");

      const payload =
        res?.data?.payload ||
        res?.data?.Payload ||
        res?.data?.data ||
        res?.data?.Data ||
        {};
      const token = payload?.verificationToken || payload?.VerificationToken || "";
      if (!token) throw new Error("OTP verification token missing.");

      setVerificationToken(token);
      setOtpVerified(true);
      toast.success("Mobile number verified.");
    } catch (err) {
      setError(friendlyOtpError(err?.response?.data?.message || err?.message));
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResetPassword = async e => {
    e.preventDefault();
    setError("");

    if (!canReset) {
      setError("Complete OTP verification and enter matching passwords.");
      return;
    }

    setLoading(true);
    try {
      const res = await axiosClient.post(
        "/auth/forgot-password/reset-via-otp",
        {
          email: email.trim().toLowerCase(),
          phone,
          newPassword,
          signupPhoneVerificationToken: verificationToken,
        },
        { __silentToast: true },
      );
      const ok = res?.data?.success ?? res?.data?.Success ?? false;
      if (!ok) throw new Error(res?.data?.message || "Password reset failed.");

      toast.success("Password reset successful. Please log in.");
      navigate(`/login?email=${encodeURIComponent(email.trim())}`, { replace: true });
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Password reset failed.");
    } finally {
      setLoading(false);
    }
  };

  const onOtpDigit = (index, value) => {
    const d = String(value || "").replace(/\D/g, "").slice(-1);
    const chars = otp.padEnd(6, " ").slice(0, 6).split("");
    chars[index] = d || " ";
    const next = chars.join("").replace(/\s/g, "").slice(0, 6);
    setOtp(next);
    if (d && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const onOtpKey = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setTimeout(
      () => setResendCooldown(prev => Math.max(prev - 1, 0)),
      1000,
    );
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-3 py-3">
      <div className="flex flex-col lg:flex-row w-full max-w-4xl shadow-xl rounded-xl overflow-hidden bg-white">
        <div className="lg:w-1/2 bg-gradient-to-br from-emerald-50 to-cyan-50 p-5 lg:p-6 flex flex-col justify-center overflow-hidden">
          <div className="mb-4">
            <div className="flex items-center gap-0.5">
              <img
                src="/logo/favicon.svg"
                alt="Logo"
                className="h-14 w-14 p-2.5 object-contain"
              />
              <span className="text-[22px] font-semibold font-brand leading-[36px] text-[#111827] tracking-tight">
                XploreByte
              </span>
            </div>
          </div>

          <div className="mb-4 overflow-hidden">
            <div className="w-full">
              <TypewriterHeadline
                phrases={["Account Security", "Password Recovery", "Secure Access"]}
                baseText="Recover your access with "
                className="text-xl lg:text-2xl font-bold text-gray-900 mb-2"
                highlightClassName="text-emerald-600"
              />
            </div>
          </div>

          <div className="bg-gradient-to-r from-emerald-600 to-indigo-600 rounded-xl p-4 text-white mb-4 shadow-md">
            <div className="mb-1">
              <span className="text-base font-bold">Password Reset</span>
            </div>
            <p className="text-emerald-100 text-xs">
              Verify your mobile number and get back to your account securely.
            </p>
          </div>

          <div className="mb-2">
            <h3 className="text-base font-bold text-gray-900 mb-3">YOU CAN CONTINUE WITH</h3>
            <div className="space-y-2">
              <div className="bg-white rounded-xl p-3 border border-gray-100 shadow-sm flex items-start space-x-3">
                <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 text-base">OTP Verification</h4>
                  <p className="text-gray-600 text-xs">
                    Validate account ownership through your registered mobile number.
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-xl p-3 border border-gray-100 shadow-sm flex items-start space-x-3">
                <div className="w-9 h-9 bg-cyan-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Zap className="w-4 h-4 text-cyan-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 text-base">Secure Update</h4>
                  <p className="text-gray-600 text-xs">
                    Set your new password only after successful OTP verification.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:w-1/2 p-5 lg:p-6 bg-white">
          <div className="mb-4 flex items-center justify-between">
            <span className="text-sm text-gray-600">Remembered password?</span>
            <Link to="/login" className="text-sm text-emerald-600 font-semibold hover:underline">
              Back to Login
            </Link>
          </div>

          <div className="mb-4">
            <h1 className="text-xl font-bold text-gray-900">Forgot password</h1>
            <p className="text-sm text-gray-600 mt-1">
              Verify your mobile number with OTP, then set a new password.
            </p>
          </div>

          {error && (
            <div className="mb-4 text-sm bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <form onSubmit={handleResetPassword} className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full px-3 h-9 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="Enter account email"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number *</label>
              <div className="flex items-center gap-2">
                <select
                  className="px-2 h-9 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 w-[100px] text-sm"
                  defaultValue="+91"
                >
                  <option value="+91">IN +91</option>
                </select>
                <input
                  type="tel"
                  value={phone}
                  onChange={handlePhoneChange}
                  inputMode="numeric"
                  maxLength={10}
                  className="flex-1 px-3 h-9 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Enter 10-digit number"
                  required
                />
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={!canSendOtp}
                  className={`px-3 h-9 rounded-lg text-sm font-medium whitespace-nowrap ${
                    canSendOtp
                      ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                      : "bg-gray-200 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  {otpLoading ? "Sending..." : "Send OTP"}
                </button>
              </div>
              {resendCooldown > 0 && (
                <p className="text-xs text-gray-500 mt-1">Resend in {resendCooldown}s</p>
              )}
            </div>

            {otpSent && !otpVerified && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Enter OTP</label>
                <div className="flex gap-2 justify-start">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <input
                      key={i}
                      ref={el => {
                        otpRefs.current[i] = el;
                      }}
                      value={otp[i] || ""}
                      onChange={e => onOtpDigit(i, e.target.value)}
                      onKeyDown={e => onOtpKey(i, e)}
                      inputMode="numeric"
                      maxLength={1}
                      className="w-10 h-10 text-center border border-gray-300 rounded-lg text-base font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  ))}
                </div>
                <button
                  type="button"
                  onClick={handleVerifyOtp}
                  disabled={otp.length !== 6 || otpLoading}
                  className={`mt-2 w-full px-4 h-9 rounded-lg text-sm font-medium ${
                    otp.length === 6 && !otpLoading
                      ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                      : "bg-gray-200 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  {otpLoading ? "Verifying..." : "Verify OTP"}
                </button>
              </div>
            )}

            {otpVerified && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Password *</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    className="w-full px-3 h-9 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Minimum 6 characters"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password *
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    className="w-full px-3 h-9 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Re-enter new password"
                    required
                  />
                </div>
              </>
            )}

            <button
              type="submit"
              disabled={!canReset}
              className={`w-full px-4 h-9 rounded-lg text-sm font-semibold ${
                canReset
                  ? "bg-gradient-to-r from-emerald-600 to-indigo-600 hover:from-emerald-700 hover:to-indigo-700 text-white"
                  : "bg-gray-200 text-gray-500 cursor-not-allowed"
              }`}
            >
              {loading ? "Updating..." : "Reset Password"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
